// @ts-check

import { Wishlist } from '@theme/lib-wishlist';
import { ThemeEvent } from '@theme/events';

/**
 * Extract product card metadata from a wishlist button element.
 * @param {HTMLElement} button
 * @returns {{title?: string, image?: string, price?: string, url?: string}}
 */
function getProductData(button) {
  return {
    title: button.dataset.productTitle || '',
    image: button.dataset.productImage || '',
    price: button.dataset.wishlistProductPrice || '',
    url: button.dataset.productUrl || '',
  };
}

/**
 * Update the visual and accessibility state of a single button element.
 * @param {HTMLElement} button
 * @param {Set<string>} wishlistedIds
 */
function updateButtonState(button, wishlistedIds) {
  const productId = button.dataset.productId;
  if (!productId) return;
  const pressed = wishlistedIds.has(productId);

  if (pressed) {
    Wishlist.remember(productId, getProductData(button));
  }

  button.setAttribute('aria-pressed', pressed ? 'true' : 'false');

  if (pressed && button.dataset.labelRemove) {
    button.setAttribute('aria-label', button.dataset.labelRemove);
  } else if (!pressed && button.dataset.labelAdd) {
    button.setAttribute('aria-label', button.dataset.labelAdd);
  }

  if (pressed) {
    button.classList.add('is-wishlisted');
  } else {
    button.classList.remove('is-wishlisted');
  }

  const labelEl = button.querySelector('[data-wishlist-label]');
  if (labelEl) {
    labelEl.textContent = pressed
      ? button.dataset.labelRemove || ''
      : button.dataset.labelAdd || '';
  }
}

/**
 * Build a Set of wishlisted product IDs from storage (single read).
 * @returns {Set<string>}
 */
function getWishlistedIdSet() {
  return new Set(Wishlist.getDetails().map((item) => item.id));
}

/**
 * Sync every wishlist button on the page (batched single storage read).
 */
function initAllButtons() {
  const wishlistedIds = getWishlistedIdSet();
  /** @type {NodeListOf<HTMLElement>} */
  const buttons = document.querySelectorAll('[data-action="toggle-wishlist"]');
  buttons.forEach((button) => updateButtonState(button, wishlistedIds));
}

/**
 * Sync wishlist buttons for one product id (desktop + mobile + quick-view).
 * @param {string} productId
 */
function updateButtonsForProduct(productId) {
  const wishlistedIds = getWishlistedIdSet();
  /** @type {NodeListOf<HTMLElement>} */
  const buttons = document.querySelectorAll(
    `[data-action="toggle-wishlist"][data-product-id="${productId}"]`
  );
  buttons.forEach((button) => updateButtonState(button, wishlistedIds));
}

document.addEventListener('dialog:open', () => {
  initAllButtons();
});

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const button = target.closest('[data-action="toggle-wishlist"]');
  if (!(button instanceof HTMLElement)) return;
  event.preventDefault();
  const productId = button.dataset.productId;
  if (!productId) return;

  Wishlist.toggle(productId, getProductData(button));
});

document.addEventListener('DOMContentLoaded', () => {
  initAllButtons();
});

window.addEventListener(ThemeEvent.WishlistChanged, (event) => {
  if (!(event instanceof CustomEvent)) return;
  const productId = event.detail?.productId;

  if (productId === null || productId === undefined) {
    initAllButtons();
    return;
  }

  updateButtonsForProduct(String(productId));
});

window.addEventListener(ThemeEvent.WishlistRefresh, () => {
  initAllButtons();
});
